import React, { useCallback, useEffect, useState } from "react";
import "./analytics.css";
import { getCategoryRun } from "./analyticsApi";
import { Progress } from "./parts";
import { RUN_STATUS, compact, dateTime, duration, fmt } from "./format";

/// Always-visible strip at the top of the category dashboard: is the clock
/// running, how long is left this month, and how much the category's users
/// collected toward the goal so far. The admin starts / pauses / stops the
/// category; this is read-only. Refreshes every minute.
export default function RunStatusBar({ refreshKey }) {
  const [run, setRun] = useState(null);
  const [left, setLeft] = useState(0);

  const load = useCallback(() => {
    getCategoryRun()
      .then((r) => setRun(r))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load, refreshKey]);

  const status = run?.run_status || "stopped";
  const cycle = run?.current_cycle || null;

  // Live countdown while running; frozen while paused.
  useEffect(() => {
    const base = cycle?.seconds_left ?? 0;
    setLeft(base);
    if (status !== "running" || !cycle) return undefined;
    const started = Date.now();
    const t = setInterval(() => setLeft(Math.max(0, base - Math.floor((Date.now() - started) / 1000))), 1000);
    return () => clearInterval(t);
  }, [cycle, status]);

  if (!run) return null;
  const st = RUN_STATUS[status] || RUN_STATUS.stopped;

  return (
    <div className="rv-wrap mb-4">
      <div className="rv-panel rv-runbar">
        <div className="rv-runbar-head">
          <span className={`rv-badge ${st.cls}`} style={{ fontSize: "0.9rem" }}>
            <span className="rv-dot" style={{ background: st.dot, width: 9, height: 9 }} />
            {st.label}
          </span>
          <span className="text-grey small">
            {status === "running" && run.run_started_at && <>Counting since {dateTime(run.run_started_at)}</>}
            {status === "paused" && <>Paused by the admin {run.run_status_changed_at ? `on ${dateTime(run.run_status_changed_at)}` : ""}</>}
            {status === "stopped" && <>Not counting — the admin hasn't started your category</>}
          </span>
        </div>

        {status === "stopped" ? (
          <div className="text-grey small mt-2" style={{ lineHeight: 1.6 }}>
            While stopped, your users' gifts don't pay your 10% / 20% share and don't count toward your goal
            {run.category?.cycle_goal_coins ? ` of ${compact(run.category.cycle_goal_coins)} coins` : ""}. When the admin
            starts it, a {run.category?.cycle_months || 1}-month cycle begins from zero.
          </div>
        ) : cycle ? (
          <div className="rv-runbar-grid mt-3">
            <div>
              <div className="rv-kpi-label">Collected this month</div>
              <div className="rv-runbar-value">
                {fmt(cycle.achieved_coins)} <span className="text-grey fs-6">/ {compact(cycle.goal_coins)}</span>
              </div>
              <Progress value={cycle.progress} tone={cycle.status === "achieved" ? "gold" : ""} size="sm" />
              <div className="text-grey small mt-1">
                {cycle.status === "achieved" ? "Goal reached" : `${cycle.progress}% · ${compact(cycle.remaining_coins)} to go`}
              </div>
            </div>
            <div>
              <div className="rv-kpi-label">{status === "paused" ? "Time left (frozen)" : "Time left"}</div>
              <div className="rv-runbar-value" style={{ fontVariantNumeric: "tabular-nums" }}>
                {duration(left)}
              </div>
              <div className="rv-run-time">
                <span style={{ width: `${cycle.time_percent || 0}%` }} />
              </div>
              <div className="text-grey small mt-1">Ends {dateTime(cycle.ends_at)}</div>
            </div>
            <div>
              <div className="rv-kpi-label">Held for you (pending)</div>
              <div className="rv-runbar-value">{fmt(cycle.pending_coins)}</div>
              <div className="text-grey small">
                {cycle.status === "achieved"
                  ? "Your 20% is now paid straight away"
                  : "Paid to you the moment you reach the goal this month"}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
